import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useHistory } from 'react-router-dom';
import {
  Box,
  Paper,
  Typography,
  Grid,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Chip,
  Card,
  CardContent,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  InputAdornment,
  Autocomplete,
  IconButton,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Divider,
  Avatar,
  CircularProgress,
} from '@mui/material';
import { 
  Search as SearchIcon,
  TrendingUp as TrendingUpIcon,
  CheckCircle as CheckCircleIcon,
  Schedule as ScheduleIcon,
  Visibility,
  Close,
  Person,
  CurrencyRupee as CurrencyRupeeIcon,
  CalendarToday,
  Refresh as RefreshIcon,
} from '@mui/icons-material';
import { getBookingCommission } from '../../services/reports';
import { getAssociates } from '../../services/associate';
import { getBookings } from '../../services/project';
import ExportButton from '../../components/ExportButton/ExportButton';
import RefreshButton from '../../components/RefreshButton/RefreshButton';
import { exportAsPDF, exportAsCSV, exportAsExcel } from '../../utils/importExport';

const BookingCommission = () => {
  const history = useHistory();
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [filters, setFilters] = useState({
    fromDate: '',
    toDate: '',
    status: '',
    associate: '',
    searchTerm: '',
  });
  const [selectedAssociate, setSelectedAssociate] = useState(null);
  const [selectedBooking, setSelectedBooking] = useState(null);

  const [viewDialogOpen, setViewDialogOpen] = useState(false);
  const [selectedCommission, setSelectedCommission] = useState(null);

  // API data states
  const [commissionData, setCommissionData] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [loading, setLoading] = useState(false);
  
  // Autocomplete states
  const [associateOptions, setAssociateOptions] = useState([]);
  const [associateSearchInput, setAssociateSearchInput] = useState('');
  const [loadingAssociates, setLoadingAssociates] = useState(false);
  const associateFetchingRef = useRef(false);
  const associateInitialLoadRef = useRef(false);
  const [associatePage, setAssociatePage] = useState(1);
  const [associateHasMore, setAssociateHasMore] = useState(true);
  const associateSearchTermRef = useRef('');
  
  const [bookingOptions, setBookingOptions] = useState([]);
  const [bookingSearchInput, setBookingSearchInput] = useState('');
  const [loadingBookings, setLoadingBookings] = useState(false);
  const bookingFetchingRef = useRef(false);
  const bookingInitialLoadRef = useRef(false);
  const [bookingPage, setBookingPage] = useState(1);
  const [bookingHasMore, setBookingHasMore] = useState(true);
  const bookingSearchTermRef = useRef('');
  
  // Customer dropdown states
  const [customerOptions, setCustomerOptions] = useState([]);
  const [customerSearchInput, setCustomerSearchInput] = useState('');
  const [loadingCustomers, setLoadingCustomers] = useState(false);
  const customerFetchingRef = useRef(false);
  const customerInitialLoadRef = useRef(false);
  const [customerPage, setCustomerPage] = useState(1);
  const [customerHasMore, setCustomerHasMore] = useState(true);
  const customerSearchTermRef = useRef('');
  const [selectedCustomer, setSelectedCustomer] = useState(null);

  // Map API response to component data structure
  const mapCommissionData = (item, index, currentPage, currentRowsPerPage) => {
    // commissionAmount from API is the plot amount
    // netCommissionAmount is the actual commission earned
    const plotAmount = item.commissionAmount || 0;
    
    return {
      sno: currentPage * currentRowsPerPage + index + 1,
      bookingId: item.bookingId || '',
      customerName: item.customerName || '',
      plotNo: item.inventoryName || '',
      plotAmount: plotAmount,
      commissionRate: item.netCommissionPercent || 0,
      commissionAmount: item.netCommissionAmount || 0,
      associate: item.associateName || '',
      status: item.due > 0 ? 'Pending' : 'Paid',
      // Additional fields from API
      associateCommissionPercent: item.associateCommissionPercent || 0,
      bookingCommissionPercent: item.bookingCommissionPercent || 0,
      discountAmount: item.discountAmount || 0,
      commissionAmountpaid: item.commissionAmountpaid || 0,
      due: item.due || 0,
      availableAmount: item.availableAmount || 0,
      projectName: item.projectName || '',
      inventoryType: item.inventoryType || '',
      bookingDate: item.bookingDate || '',
      bookingObjectId: item.bookingObjectId || '',
      createdAt: item.createdAt || '',
      _raw: item, // Store raw data for dialog
    };
  };

  // Fetch booking commission data
  const fetchCommissionData = useCallback(async () => {
    setLoading(true);
    try {
      const params = {
        page: page + 1,
        limit: rowsPerPage,
      };

      // Add associate filter if selected
      if (selectedAssociate && selectedAssociate.id) {
        params.associateId = selectedAssociate.id;
      }

      // Add booking filter if selected
      if (selectedBooking && selectedBooking.id) {
        params.bookingId = selectedBooking.id;
      }

      // Add customer filter if selected
      if (selectedCustomer && selectedCustomer.id) {
        params.customerId = selectedCustomer.id;
      }

      // Add status filter if selected
      if (filters.status) {
        params.status = filters.status.toLowerCase();
      }

      const response = await getBookingCommission(params);

      if (response.success && Array.isArray(response.data)) {
        const mappedData = response.data.map((item, index) => mapCommissionData(item, index, page, rowsPerPage));
        setCommissionData(mappedData);
        setTotalCount(parseInt(response.extra?.total || response.data.length || 0, 10));
      } else {
        setCommissionData([]);
        setTotalCount(0);
      }
    } catch (error) {
      console.error('Error fetching booking commission:', error);
      setCommissionData([]);
      setTotalCount(0);
    } finally {
      setLoading(false);
    }
  }, [page, rowsPerPage, selectedAssociate, selectedBooking, selectedCustomer, filters.status]);

  // Fetch customers for autocomplete with pagination
  const fetchCustomers = useCallback(async (searchTerm = '', pageNum = 1, append = false) => {
    if (customerFetchingRef.current) return;
    if (!append && !customerHasMore && pageNum > 1) return;
    
    customerFetchingRef.current = true;
    setLoadingCustomers(true);
    try {
      const base = process.env.REACT_APP_API_BASE_URL;
      const token = localStorage.getItem('token');
      const params = new URLSearchParams({
        page: String(pageNum),
        limit: '20',
      });
      
      if (searchTerm && searchTerm.trim().length > 0) {
        params.append('search', searchTerm.trim());
      }

      const response = await fetch(`${base}/api/customers?${params.toString()}`, {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });
      
      const result = await response.json();
      
      let customers = [];
      let total = 0;
      
      if (result.success && Array.isArray(result.data)) {
        customers = result.data;
        total = parseInt(result.extra?.total || customers.length, 10);
      }

      const formattedCustomers = customers.map((customer) => ({
        id: customer._id || customer.id,
        name: customer.name || '',
        number: String(customer.customerNo || ''),
        mobile: String(customer.phone || customer.whatsapp || ''),
        email: customer.email || '',
        customerId: customer._id || customer.id,
        _raw: customer,
      }));

      if (append) {
        setCustomerOptions(prev => {
          const newOptions = [...prev, ...formattedCustomers];
          const currentTotal = newOptions.length;
          const hasMore = currentTotal < total && formattedCustomers.length >= 20;
          setCustomerHasMore(hasMore);
          return newOptions;
        });
      } else {
        setCustomerOptions(formattedCustomers);
        const currentTotal = formattedCustomers.length;
        const hasMore = currentTotal < total && formattedCustomers.length >= 20;
        setCustomerHasMore(hasMore);
      }
    } catch (error) {
      console.error('Error fetching customers:', error);
      if (!append) {
        setCustomerOptions([]);
        setCustomerHasMore(false);
      }
    } finally {
      setLoadingCustomers(false);
      customerFetchingRef.current = false;
    }
  }, []);

  // Pre-load customers on mount so dropdown is ready immediately
  useEffect(() => {
    fetchCustomers('', 1, false);
    customerInitialLoadRef.current = true;
  }, [fetchCustomers]);

  // Debounced customer search — fires on any input (no minimum chars)
  useEffect(() => {
    const trimmedInput = customerSearchInput.trim();

    // If cleared, restore full list
    if (trimmedInput.length === 0) {
      if (customerInitialLoadRef.current && customerSearchTermRef.current !== '') {
        customerSearchTermRef.current = '';
        setCustomerPage(1);
        setCustomerHasMore(true);
        fetchCustomers('', 1, false);
      }
      return;
    }

    if (customerSearchTermRef.current !== trimmedInput) {
      setCustomerPage(1);
      setCustomerHasMore(true);
      customerSearchTermRef.current = trimmedInput;
    }

    const timeoutId = setTimeout(() => {
      fetchCustomers(trimmedInput, 1, false);
    }, 300);

    return () => clearTimeout(timeoutId);
  }, [customerSearchInput, fetchCustomers]);

  // Fetch associates for autocomplete with pagination
  const fetchAssociates = useCallback(async (searchTerm = '', pageNum = 1, append = false) => {
    if (associateFetchingRef.current) return;
    if (!append && !associateHasMore && pageNum > 1) return;
    
    associateFetchingRef.current = true;
    setLoadingAssociates(true);
    try {
      const params = {
        page: pageNum,
        limit: 20, // Default limit for pagination
      };
      
      if (searchTerm && searchTerm.trim().length > 0) {
        params.filterKey = searchTerm.trim();
      }

      const response = await getAssociates(params);
      
      let associates = [];
      let total = 0;
      
      // Extract data and total from response
      if (response.status && Array.isArray(response.data)) {
        associates = response.data;
        // Check for total in extra object first, then fallback to other locations
        total = parseInt(response.extra?.total || response.totalCount || response.data?.totalDocs || response.count || associates.length, 10);
      } else if (response.success && Array.isArray(response.data)) {
        associates = response.data;
        total = parseInt(response.extra?.total || response.totalCount || response.data?.totalDocs || response.count || associates.length, 10);
      } else if (Array.isArray(response.data?.docs)) {
        associates = response.data.docs;
        total = parseInt(response.extra?.total || response.data.totalDocs || response.totalCount || associates.length, 10);
      }

      const formattedAssociates = associates.map((associate) => ({
        id: associate._id || associate.id,
        name: associate.name || '',
        number: String(associate.associateId || associate._id || ''),
        phone: String(associate.phone || associate.whatsapp || ''),
      }));

      if (append) {
        setAssociateOptions(prev => {
          const newOptions = [...prev, ...formattedAssociates];
          const currentTotal = newOptions.length;
          // Check if there are more pages: current total < total AND we got a full page of results
          const hasMore = currentTotal < total && formattedAssociates.length >= params.limit;
          setAssociateHasMore(hasMore);
          return newOptions;
        });
      } else {
        setAssociateOptions(formattedAssociates);
        const currentTotal = formattedAssociates.length;
        // Check if there are more pages: current total < total AND we got a full page of results
        const hasMore = currentTotal < total && formattedAssociates.length >= params.limit;
        setAssociateHasMore(hasMore);
      }
    } catch (error) {
      console.error('Error fetching associates:', error);
      if (!append) {
        setAssociateOptions([]);
        setAssociateHasMore(false);
      }
    } finally {
      setLoadingAssociates(false);
      associateFetchingRef.current = false;
    }
  }, []);

  // Fetch bookings for autocomplete with pagination
  const fetchBookings = useCallback(async (searchTerm = '', pageNum = 1, append = false) => {
    if (bookingFetchingRef.current) return;
    if (!append && !bookingHasMore && pageNum > 1) return;
    
    bookingFetchingRef.current = true;
    setLoadingBookings(true);
    try {
      const params = {
        page: pageNum,
        limit: 20, // Default limit for pagination
      };
      
      if (searchTerm && searchTerm.trim().length > 0) {
        params.search = searchTerm.trim();
      }

      const response = await getBookings(params);
      
      let bookings = [];
      let total = 0;
      
      if (response.success || response.status === true) {
        bookings = Array.isArray(response.data) ? response.data : (response.data?.docs || []);
        total = response.totalCount || response.data?.totalDocs || response.count || bookings.length;
      } else if (Array.isArray(response)) {
        bookings = response;
        total = bookings.length;
      } else if (response.data) {
        bookings = Array.isArray(response.data) ? response.data : (response.data?.docs || []);
        total = response.totalCount || response.data?.totalDocs || response.count || bookings.length;
      }

      const formattedBookings = bookings.map((booking) => ({
        id: booking._id || booking.id || booking.bookingId,
        bookingId: booking.bookingId || booking.bookingNo || '',
        customerName: booking.customerName || booking.customer?.name || '',
        displayLabel: `Booking #${booking.bookingId || booking.bookingNo || ''} - ${booking.customerName || booking.customer?.name || ''}`,
      }));

      if (append) {
        setBookingOptions(prev => {
          const newOptions = [...prev, ...formattedBookings];
          // Check if there are more pages
          const hasMore = newOptions.length < total && formattedBookings.length === params.limit;
          setBookingHasMore(hasMore);
          return newOptions;
        });
      } else {
        setBookingOptions(formattedBookings);
        // Check if there are more pages
        const hasMore = formattedBookings.length < total && formattedBookings.length === params.limit;
        setBookingHasMore(hasMore);
      }
    } catch (error) {
      console.error('Error fetching bookings:', error);
      if (!append) {
        setBookingOptions([]);
        setBookingHasMore(false);
      }
    } finally {
      setLoadingBookings(false);
      bookingFetchingRef.current = false;
    }
  }, []);

  // Initial load and when filters change
  useEffect(() => {
    fetchCommissionData();
  }, [fetchCommissionData]);

  // Debounced associate search - only fetch when user types (length >= 2)
  useEffect(() => {
    const trimmedInput = associateSearchInput.trim();
    
    // Don't fetch on initial empty load or if input is too short
    if (trimmedInput.length === 0) {
      // Only fetch on initial load once
      if (!associateInitialLoadRef.current) {
        associateInitialLoadRef.current = true;
        // Don't fetch empty - wait for user input
        return;
      }
      return;
    }

    // Only fetch if user has typed at least 2 characters
    if (trimmedInput.length < 2) {
      return;
    }

    // Reset pagination when search term changes
    if (associateSearchTermRef.current !== trimmedInput) {
      setAssociatePage(1);
      setAssociateHasMore(true);
      associateSearchTermRef.current = trimmedInput;
    }

    const timeoutId = setTimeout(() => {
      fetchAssociates(trimmedInput, 1, false);
    }, 500);

    return () => clearTimeout(timeoutId);
  }, [associateSearchInput, fetchAssociates]);

  // Debounced booking search - only fetch when user types (length >= 2)
  useEffect(() => {
    const trimmedInput = bookingSearchInput.trim();
    
    // Don't fetch on initial empty load or if input is too short
    if (trimmedInput.length === 0) {
      // Only fetch on initial load once
      if (!bookingInitialLoadRef.current) {
        bookingInitialLoadRef.current = true;
        // Don't fetch empty - wait for user input
        return;
      }
      return;
    }

    // Only fetch if user has typed at least 2 characters
    if (trimmedInput.length < 2) {
      return;
    }

    // Reset pagination when search term changes
    if (bookingSearchTermRef.current !== trimmedInput) {
      setBookingPage(1);
      setBookingHasMore(true);
      bookingSearchTermRef.current = trimmedInput;
    }

    const timeoutId = setTimeout(() => {
      fetchBookings(trimmedInput, 1, false);
    }, 500);

    return () => clearTimeout(timeoutId);
  }, [bookingSearchInput, fetchBookings]);

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleReset = () => {
    setFilters({
      fromDate: '',
      toDate: '',
      status: '',
      associate: '',
      searchTerm: '',
    });
    setSelectedAssociate(null);
    setSelectedBooking(null);
    setSelectedCustomer(null);
    setAssociateSearchInput('');
    setBookingSearchInput('');
    setCustomerSearchInput('');
    setPage(0);
  };

  const handleSearch = () => {
    setPage(0);
    fetchCommissionData();
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  // Export functions

  const buildExportRows = () => {
    return commissionData.map((commission, index) => ({
      'S.No': index + 1,
      'Booking ID': commission.bookingId || '',
      'Customer': commission.customerName || '',
      'Plot No': commission.plotNo || '',
      'Plot Amount': commission.plotAmount || 0,
      'Commission %': commission.commissionRate || 0,
      'Commission Amount': commission.commissionAmount || 0,
      'Associate': commission.associate || '',
      'Status': commission.status || '',
    }));
  };

  const getExportFileName = () => `booking_commissions_${new Date().toISOString().split('T')[0]}`;

  const handleExportCsv = () => {
    if (commissionData.length === 0) return;
    exportAsCSV({
      rows: buildExportRows(),
      fileNameBase: getExportFileName()
    });
  };

  const handleExportPdf = () => {
    if (commissionData.length === 0) return;
    const formattedRows = buildExportRows().map(r => ({
      ...r,
      'Plot Amount': `Rs. ${r['Plot Amount'].toLocaleString()}`,
      'Commission Amount': `Rs. ${r['Commission Amount'].toLocaleString()}`,
    }));

    exportAsPDF({
      rows: formattedRows,
      title: 'Booking Commission Report',
      fileNameBase: getExportFileName(),
      orientation: 'l'
    });
  };

  const handleExportExcel = () => {
    if (commissionData.length === 0) return;
    exportAsExcel({
      rows: buildExportRows(),
      sheetName: 'Booking Commissions',
      fileNameBase: getExportFileName()
    });
  };

  // View function
  const handleView = (row) => {
    setSelectedCommission(row);
    setViewDialogOpen(true);
  };

  const handleCloseViewDialog = () => {
    setViewDialogOpen(false);
    setSelectedCommission(null);
  };

  // Filter data based on current filters (client-side filtering for status and search term)
  const filteredData = useMemo(() => {
    return commissionData.filter(item => {
    const matchesStatus = filters.status === '' || item.status === filters.status;
    const matchesSearch = filters.searchTerm === '' || 
      item.customerName.toLowerCase().includes(filters.searchTerm.toLowerCase()) ||
        String(item.bookingId).toLowerCase().includes(filters.searchTerm.toLowerCase()) ||
      item.plotNo.toLowerCase().includes(filters.searchTerm.toLowerCase()) ||
        item.associate.toLowerCase().includes(filters.searchTerm.toLowerCase());
      
      return matchesStatus && matchesSearch;
    });
  }, [commissionData, filters.status, filters.searchTerm]);

  const totalCommission = useMemo(() => {
    return filteredData.reduce((sum, item) => sum + item.commissionAmount, 0);
  }, [filteredData]);
  
  const paidCommission = useMemo(() => {
    return filteredData.filter(item => item.status === 'Paid').reduce((sum, item) => sum + item.commissionAmount, 0);
  }, [filteredData]);
  
  const pendingCommission = useMemo(() => {
    return filteredData.filter(item => item.status === 'Pending').reduce((sum, item) => sum + item.commissionAmount, 0);
  }, [filteredData]);

  return (
    <Box sx={{ p: 3, backgroundColor: '#f5f5f5', minHeight: '100vh' }}>
      {/* Header */}
      <Box sx={{ mb: 4 }}>
        <Box display="flex" justifyContent="space-between" alignItems="center" flexWrap="wrap" gap={2}>
          <Box>
            <Typography variant="h4" sx={{ fontWeight: 700, color: '#000', mb: 0.5 }}>
              Booking Commission Report
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Track commission payments for bookings
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', gap: 1.2, alignItems: 'center' }}>
            <ExportButton
              onExportPdf={handleExportPdf}
              onExportCsv={handleExportCsv}
              onExportExcel={handleExportExcel}
            />
            <RefreshButton
              onClick={() => fetchCommissionData()}
              loading={loading}
              sx={{
                fontWeight: 600,
                px: 2,
                fontSize: '15px',
                borderRadius: 2,
                height: { xs: 'auto', sm: 40 }
              }}
            />
          </Box>
        </Box>
      </Box>

      {/* Summary Cards */}
      

      {/* Search Section */}
      <Card sx={{ mb: 3, boxShadow: '0 2px 8px rgba(0,0,0,0.08)', borderRadius: 2 }}>
        <CardContent>
          <Typography variant="h6" sx={{ fontWeight: 700, mb: 2 }}>
            Search & Filters
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={12} md={3}>
              <Autocomplete
                options={associateOptions}
                getOptionLabel={(option) => option.name ? `${option.name} (${option.number})` : ''}
                value={selectedAssociate}
                onChange={(event, newValue) => {
                  setSelectedAssociate(newValue);
                  setPage(0);
                }}
                onInputChange={(event, newInputValue, reason) => {
                  if (reason === 'input' || reason === 'clear') {
                    setAssociateSearchInput(newInputValue);
                    // Reset pagination when input changes
                    if (reason === 'clear' || (reason === 'input' && newInputValue.trim().length < 2)) {
                      setAssociatePage(1);
                      setAssociateHasMore(true);
                      associateSearchTermRef.current = '';
                    }
                  }
                }}
                onOpen={() => {
                  // Fetch initial options when dropdown opens if we don't have any
                  if (associateOptions.length === 0 && !associateFetchingRef.current) {
                    setAssociatePage(1);
                    setAssociateHasMore(true);
                    fetchAssociates(associateSearchInput || '', 1, false);
                  }
                }}
                loading={loadingAssociates}
                ListboxProps={{
                  onScroll: (event) => {
                    const listboxNode = event.currentTarget;
                    // Check if scrolled to bottom (within 50px)
                    const scrollTop = listboxNode.scrollTop;
                    const scrollHeight = listboxNode.scrollHeight;
                    const clientHeight = listboxNode.clientHeight;
                    const scrollBottom = scrollTop + clientHeight;
                    
                    // Trigger load more when within 50px of bottom
                    if (scrollHeight > clientHeight && scrollBottom >= scrollHeight - 50) {
                      if (associateHasMore && !loadingAssociates && !associateFetchingRef.current) {
                        const nextPage = associatePage + 1;
                        setAssociatePage(nextPage);
                        fetchAssociates(associateSearchInput || '', nextPage, true);
                      }
                    }
                  },
                }}
                filterOptions={(options, { inputValue }) => {
                  const searchTerm = inputValue.toLowerCase();
                  return options.filter(option =>
                    (option.name || '').toLowerCase().includes(searchTerm) ||
                    String(option.number || '').toLowerCase().includes(searchTerm)
                  );
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Search Associate"
                    size="small"
                    placeholder="Type name or number..."
                    InputProps={{
                      ...params.InputProps,
                      startAdornment: (
                        <InputAdornment position="start">
                          <SearchIcon color="action" />
                        </InputAdornment>
                      ),
                      endAdornment: (
                        <React.Fragment>
                          {loadingAssociates ? <CircularProgress color="inherit" size={20} /> : null}
                          {params.InputProps.endAdornment}
                        </React.Fragment>
                      ),
                    }}
                  />
                )}
                renderOption={(props, option) => (
                  <Box component="li" {...props}>
                    <Box>
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>
                        {option.name}
                      </Typography>
                      <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 500 }}>
                        {option.number} {option.phone ? `• ${option.phone}` : ''}
                      </Typography>
                    </Box>
                  </Box>
                )}
                noOptionsText="No associates found"
                clearOnEscape
                selectOnFocus
                handleHomeEndKeys
              />
            </Grid>
            <Grid item xs={12} md={3}>
              <Autocomplete
                options={bookingOptions}
                getOptionLabel={(option) => option.displayLabel || `Booking #${option.bookingId}`}
                value={selectedBooking}
                onChange={(event, newValue) => {
                  setSelectedBooking(newValue);
                  setPage(0);
                }}
                onInputChange={(event, newInputValue, reason) => {
                  if (reason === 'input' || reason === 'clear') {
                    setBookingSearchInput(newInputValue);
                    // Reset pagination when input changes
                    if (reason === 'clear' || (reason === 'input' && newInputValue.trim().length < 2)) {
                      setBookingPage(1);
                      setBookingHasMore(true);
                      bookingSearchTermRef.current = '';
                    }
                  }
                }}
                onOpen={() => {
                  // Fetch initial options when dropdown opens if we don't have any
                  if (bookingOptions.length === 0 && !bookingFetchingRef.current) {
                    setBookingPage(1);
                    setBookingHasMore(true);
                    fetchBookings(bookingSearchInput || '', 1, false);
                  }
                }}
                loading={loadingBookings}
                ListboxProps={{
                  onScroll: (event) => {
                    const listboxNode = event.currentTarget;
                    // Check if scrolled to bottom (within 50px)
                    if (listboxNode.scrollTop + listboxNode.clientHeight >= listboxNode.scrollHeight - 50) {
                      if (bookingHasMore && !loadingBookings && !bookingFetchingRef.current) {
                        const nextPage = bookingPage + 1;
                        setBookingPage(nextPage);
                        fetchBookings(bookingSearchInput || '', nextPage, true);
                      }
                    }
                  },
                }}
                filterOptions={(options, { inputValue }) => {
                  return options.filter(option =>
                    String(option.bookingId).toLowerCase().includes(inputValue.toLowerCase()) ||
                    option.customerName.toLowerCase().includes(inputValue.toLowerCase())
                  );
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Search Booking"
                    size="small"
                    placeholder="Type booking ID or customer name..."
                    InputProps={{
                      ...params.InputProps,
                      startAdornment: (
                        <InputAdornment position="start">
                          <SearchIcon color="action" />
                        </InputAdornment>
                      ),
                      endAdornment: (
                        <React.Fragment>
                          {loadingBookings ? <CircularProgress color="inherit" size={20} /> : null}
                          {params.InputProps.endAdornment}
                        </React.Fragment>
                      ),
                    }}
                  />
                )}
                renderOption={(props, option) => (
                  <Box component="li" {...props}>
                    <Box>
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>
                        Booking #{option.bookingId}
                      </Typography>
                      <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 500 }}>
                        {option.customerName}
                      </Typography>
                    </Box>
                  </Box>
                )}
                noOptionsText="No bookings found"
                clearOnEscape
                selectOnFocus
                handleHomeEndKeys
              />
            </Grid>
            <Grid item xs={12} md={3}>
              <Autocomplete
                options={customerOptions}
                getOptionLabel={(option) => option.name ? `${option.name} (${option.number})` : ''}
                value={selectedCustomer}
                onChange={(event, newValue) => {
                  setSelectedCustomer(newValue);
                  setPage(0);
                }}
                onInputChange={(event, newInputValue, reason) => {
                  if (reason === 'input' || reason === 'clear') {
                    setCustomerSearchInput(newInputValue);
                  }
                }}
                onOpen={() => {
                  if (customerOptions.length === 0 && !customerFetchingRef.current) {
                    fetchCustomers('', 1, false);
                  }
                }}
                loading={loadingCustomers}
                ListboxProps={{
                  onScroll: (event) => {
                    const listboxNode = event.currentTarget;
                    const scrollTop = listboxNode.scrollTop;
                    const scrollHeight = listboxNode.scrollHeight;
                    const clientHeight = listboxNode.clientHeight;
                    const scrollBottom = scrollTop + clientHeight;
                    
                    if (scrollHeight > clientHeight && scrollBottom >= scrollHeight - 50) {
                      if (customerHasMore && !loadingCustomers && !customerFetchingRef.current) {
                        const nextPage = customerPage + 1;
                        setCustomerPage(nextPage);
                        fetchCustomers(customerSearchInput || '', nextPage, true);
                      }
                    }
                  },
                }}
                filterOptions={(options, { inputValue }) => {
                  const searchTerm = inputValue.toLowerCase();
                  return options.filter(option =>
                    (option.name || '').toLowerCase().includes(searchTerm) ||
                    String(option.number || '').toLowerCase().includes(searchTerm) ||
                    String(option.mobile || '').includes(searchTerm)
                  );
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Search Customer"
                    size="small"
                    placeholder="Type name, number, or mobile..."
                    InputProps={{
                      ...params.InputProps,
                      startAdornment: (
                        <InputAdornment position="start">
                          <SearchIcon color="action" />
                        </InputAdornment>
                      ),
                      endAdornment: (
                        <React.Fragment>
                          {loadingCustomers ? <CircularProgress color="inherit" size={20} /> : null}
                          {params.InputProps.endAdornment}
                        </React.Fragment>
                      ),
                    }}
                  />
                )}
                renderOption={(props, option) => (
                  <Box component="li" {...props}>
                    <Box>
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>
                        {option.name}
                      </Typography>
                      <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 500 }}>
                        {option.number} {option.mobile ? `• ${option.mobile}` : ''}
                      </Typography>
                    </Box>
                  </Box>
                )}
                noOptionsText="No customers found"
                clearOnEscape
                selectOnFocus
              />
            </Grid>
            {/* <Grid item xs={12} md={3}>
              <TextField
                fullWidth
                size="small"
                label="Search Bookings"
                placeholder="Search by booking ID, plot no..."
                value={filters.searchTerm}
                onChange={(e) => {
                  setFilters({ ...filters, searchTerm: e.target.value });
                  setPage(0);
                }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon color="action" />
                    </InputAdornment>
                  ),
                }}
              />
            </Grid> */}
            <Grid item xs={12} md={3}>
              <FormControl fullWidth size="small">
                <InputLabel>Status</InputLabel>
                <Select 
                  label="Status" 
                  value={filters.status}
                  onChange={(e) => {
                    setFilters({ ...filters, status: e.target.value });
                    setPage(0);
                  }}
                >
                  <MenuItem value="">All Status</MenuItem>
                  <MenuItem value="Paid">Paid</MenuItem>
                  <MenuItem value="Pending">Pending</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            
            {/* Buttons Section */}
            <Grid item xs={12} md={3}>
              <Box sx={{ display: 'flex', gap: 1 }}>
                <Button
                  fullWidth
                  variant="contained"
                  onClick={handleSearch}
                  disabled={loading}
                  startIcon={loading ? <CircularProgress size={20} color="inherit" /> : <SearchIcon />}
                  sx={{ height: 40, textTransform: 'none', fontWeight: 600, borderRadius: 1.5 }}
                >
                  {loading ? 'Searching...' : 'Search'}
                </Button>
                <Button
                  fullWidth
                  variant="outlined"
                  onClick={handleReset}
                  sx={{ height: 40, textTransform: 'none', fontWeight: 600, borderRadius: 1.5 }}
                >
                  Reset
                </Button>
              </Box>
            </Grid>
          </Grid>
          
          {/* Search Results Summary */}
          {(selectedAssociate || selectedBooking || filters.searchTerm || filters.status) && (
            <Box sx={{ mt: 2, p: 2, backgroundColor: '#f8f9fa', borderRadius: 1, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Typography variant="body2" sx={{ fontWeight: 600, color: '#666' }}>
                {filteredData.length} commission record{filteredData.length !== 1 ? 's' : ''} found
                {selectedAssociate && ` for ${selectedAssociate.name}`}
                {selectedBooking && ` for booking #${selectedBooking.bookingId}`}
                {filters.searchTerm && ` matching "${filters.searchTerm}"`}
                {filters.status && ` with status "${filters.status}"`}
              </Typography>
            </Box>
          )}
        </CardContent>
      </Card>

      {/* Table */}
      <Card sx={{ boxShadow: '0 2px 12px rgba(0,0,0,0.1)', borderRadius: 2, mb: 3 }}>
        <CardContent>
          <Typography variant="h6" sx={{ fontWeight: 700, mb: 2 }}>
            Commission Details
          </Typography>
          <TableContainer>
            {loading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', py: 4 }}>
                <CircularProgress />
              </Box>
            ) : (
            <Table>
              <TableHead>
                <TableRow sx={{ backgroundColor: '#536DFE' }}>
                  <TableCell sx={{ color: '#fff', fontWeight: 700, fontSize: '14px' }}>S.No</TableCell>
                  <TableCell sx={{ color: '#fff', fontWeight: 700, fontSize: '14px' }}>Booking ID</TableCell>
                  <TableCell sx={{ color: '#fff', fontWeight: 700, fontSize: '14px' }}>Customer</TableCell>
                  <TableCell sx={{ color: '#fff', fontWeight: 700, fontSize: '14px' }}>Plot No</TableCell>
                  <TableCell sx={{ color: '#fff', fontWeight: 700, fontSize: '14px' }}>Plot Amount</TableCell>
                  <TableCell sx={{ color: '#fff', fontWeight: 700, fontSize: '14px' }}>Commission %</TableCell>
                  <TableCell sx={{ color: '#fff', fontWeight: 700, fontSize: '14px' }}>Commission Amount</TableCell>
                  <TableCell sx={{ color: '#fff', fontWeight: 700, fontSize: '14px' }}>Associate</TableCell>
                  <TableCell sx={{ color: '#fff', fontWeight: 700, fontSize: '14px' }}>Status</TableCell>
                  <TableCell sx={{ color: '#fff', fontWeight: 700, fontSize: '14px' }}>Action</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredData.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={10} sx={{ textAlign: 'center', py: 4 }}>
                      <Typography variant="body1" color="text.secondary">
                        No commission records found matching your search criteria
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                    filteredData.map((row, index) => (
                    <TableRow 
                      key={row.sno} 
                      hover
                      sx={{
                        '&:nth-of-type(odd)': { backgroundColor: '#f9f9f9' },
                        '&:hover': { backgroundColor: '#f0f7ff !important' }
                      }}
                    >
                        <TableCell>{row.sno}</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>
                        <Button
                          variant="text"
                          size="small"
                          sx={{ 
                            fontWeight: 600, 
                            textTransform: 'none', 
                            p: 0,
                            minWidth: 0,
                            color: 'primary.main',
                            '&:hover': {
                              textDecoration: 'underline',
                              backgroundColor: 'transparent'
                            }
                          }}
                          onClick={() => {
                            if (row.bookingObjectId) {
                              history.push(`/app/booking/BookingDetails/${row.bookingObjectId}`);
                            }
                          }}
                        >
                          {row.bookingId}
                        </Button>
                      </TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>{row.customerName}</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>{row.plotNo}</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>Rs. {row.plotAmount.toLocaleString()}</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>{row.commissionRate}%</TableCell>
                      <TableCell sx={{ fontWeight: 600, color: '#2e7d32' }}>Rs. {row.commissionAmount.toLocaleString()}</TableCell>
                      <TableCell>{row.associate}</TableCell>
                      <TableCell>
                        <Chip 
                          label={row.status} 
                          size="small" 
                          color={row.status === 'Paid' ? 'success' : 'warning'}
                          sx={{ fontWeight: 600 }}
                        />
                      </TableCell>
                      <TableCell>
                        <Tooltip title="View Details">
                          <IconButton
                            size="small"
                            onClick={() => handleView(row)}
                            sx={{ color: '#536DFE' }}
                          >
                            <Visibility fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
            )}
          </TableContainer>
          
          {/* Pagination */}
          {!loading && totalCount > 0 && (
            <TablePagination
              
              component="div"
              count={totalCount}
              rowsPerPage={rowsPerPage}
              page={page}
              onPageChange={handleChangePage}
              onRowsPerPageChange={handleChangeRowsPerPage}
            />
          )}
        </CardContent>
      </Card>

      {/* Commission Details Dialog */}
      <Dialog
        open={viewDialogOpen}
        onClose={handleCloseViewDialog}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 3,
            boxShadow: '0 8px 32px rgba(0,0,0,0.12)',
          }
        }}
      >
        <DialogTitle sx={{ 
          display: 'flex', 
          justifyContent: 'space-between', 
          alignItems: 'center',
          backgroundColor: '#536DFE',
          color: 'white',
          fontWeight: 700
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Visibility />
            <Typography variant="h6">Commission Details</Typography>
          </Box>
          <IconButton
            onClick={handleCloseViewDialog}
            sx={{ color: 'white' }}
          >
            <Close />
          </IconButton>
        </DialogTitle>

        <DialogContent sx={{ p: 3 }}>
          {selectedCommission && (
            <Box>
              {/* Header Section */}
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 3, p: 2, backgroundColor: '#f8f9fa', borderRadius: 2 }}>
                <Avatar sx={{ backgroundColor: '#536DFE', mr: 2, width: 56, height: 56 }}>
                  <CurrencyRupeeIcon sx={{ fontSize: 32 }} />
                </Avatar>
                <Box>
                  <Typography variant="h5" sx={{ fontWeight: 700, color: '#333', mb: 0.5 }}>
                    {selectedCommission.customerName}
                  </Typography>
                  <Typography variant="body1" sx={{ color: '#666', fontWeight: 600 }}>
                    Booking ID: {selectedCommission.bookingId}
                  </Typography>
                  <Chip 
                    label={selectedCommission.status} 
                    size="small" 
                    color={selectedCommission.status === 'Paid' ? 'success' : 'warning'}
                    sx={{ mt: 1, fontWeight: 600 }}
                  />
                </Box>
              </Box>

              <Divider sx={{ mb: 3 }} />

              {/* Details Grid */}
              <Grid container spacing={3}>
                {/* Booking Information */}
                <Grid item xs={12} md={6}>
                  <Card sx={{ boxShadow: '0 2px 8px rgba(0,0,0,0.08)', borderRadius: 2 }}>
                    <CardContent>
                      <Typography variant="h6" sx={{ fontWeight: 700, mb: 2, color: '#536DFE', display: 'flex', alignItems: 'center', gap: 1 }}>
                        <CalendarToday />
                        Booking Details
                      </Typography>
                      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                          <Typography variant="body2" color="text.secondary">Booking ID:</Typography>
                          <Typography variant="body2" sx={{ fontWeight: 600 }}>{selectedCommission.bookingId}</Typography>
                        </Box>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                          <Typography variant="body2" color="text.secondary">Customer Name:</Typography>
                          <Typography variant="body2" sx={{ fontWeight: 600 }}>{selectedCommission.customerName}</Typography>
                        </Box>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                          <Typography variant="body2" color="text.secondary">Plot Number:</Typography>
                          <Typography variant="body2" sx={{ fontWeight: 600 }}>{selectedCommission.plotNo}</Typography>
                        </Box>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                          <Typography variant="body2" color="text.secondary">Associate:</Typography>
                          <Typography variant="body2" sx={{ fontWeight: 600 }}>{selectedCommission.associate}</Typography>
                        </Box>
                      </Box>
                    </CardContent>
                  </Card>
                </Grid>

                {/* Financial Information */}
                <Grid item xs={12} md={6}>
                  <Card sx={{ boxShadow: '0 2px 8px rgba(0,0,0,0.08)', borderRadius: 2 }}>
                    <CardContent>
                      <Typography variant="h6" sx={{ fontWeight: 700, mb: 2, color: '#536DFE', display: 'flex', alignItems: 'center', gap: 1 }}>
                        <CurrencyRupeeIcon />
                        Commission Details
                      </Typography>
                      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                          <Typography variant="body2" color="text.secondary">Plot Amount:</Typography>
                          <Typography variant="body2" sx={{ fontWeight: 600, fontFamily: 'monospace' }}>
                            ₹{selectedCommission.plotAmount.toLocaleString()}
                          </Typography>
                        </Box>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                          <Typography variant="body2" color="text.secondary">Commission Rate:</Typography>
                          <Typography variant="body2" sx={{ fontWeight: 600 }}>
                            {selectedCommission.commissionRate}%
                          </Typography>
                        </Box>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                          <Typography variant="body2" color="text.secondary">Commission Amount:</Typography>
                          <Typography variant="body2" sx={{ fontWeight: 600, fontFamily: 'monospace', color: '#2e7d32' }}>
                            ₹{selectedCommission.commissionAmount.toLocaleString()}
                          </Typography>
                        </Box>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                          <Typography variant="body2" color="text.secondary">Status:</Typography>
                          <Chip 
                            label={selectedCommission.status} 
                            size="small" 
                            color={selectedCommission.status === 'Paid' ? 'success' : 'warning'}
                            sx={{ fontWeight: 600 }}
                          />
                        </Box>
                      </Box>
                    </CardContent>
                  </Card>
                </Grid>

                {/* Summary Information */}
                <Grid item xs={12}>
                  <Card sx={{ boxShadow: '0 2px 8px rgba(0,0,0,0.08)', borderRadius: 2 }}>
                    <CardContent>
                      <Typography variant="h6" sx={{ fontWeight: 700, mb: 2, color: '#536DFE', display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Person />
                        Summary
                      </Typography>
                      <Grid container spacing={2}>
                        <Grid item xs={12} md={4}>
                          <Box sx={{ textAlign: 'center', p: 2, backgroundColor: '#f8f9fa', borderRadius: 2 }}>
                            <Typography variant="h4" sx={{ fontWeight: 700, color: '#536DFE' }}>
                              {selectedCommission.commissionRate}%
                            </Typography>
                            <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 600 }}>
                              Commission Rate
                            </Typography>
                          </Box>
                        </Grid>
                        <Grid item xs={12} md={4}>
                          <Box sx={{ textAlign: 'center', p: 2, backgroundColor: '#f8f9fa', borderRadius: 2 }}>
                            <Typography variant="h4" sx={{ fontWeight: 700, color: '#2e7d32' }}>
                              ₹{selectedCommission.commissionAmount.toLocaleString()}
                            </Typography>
                            <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 600 }}>
                              Commission Earned
                            </Typography>
                          </Box>
                        </Grid>
                        <Grid item xs={12} md={4}>
                          <Box sx={{ textAlign: 'center', p: 2, backgroundColor: '#f8f9fa', borderRadius: 2 }}>
                            <Typography variant="h4" sx={{ fontWeight: 700, color: '#333' }}>
                              ₹{selectedCommission.plotAmount.toLocaleString()}
                            </Typography>
                            <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 600 }}>
                              Total Plot Value
                            </Typography>
                          </Box>
                        </Grid>
                      </Grid>
                    </CardContent>
                  </Card>
                </Grid>
              </Grid>
            </Box>
          )}
        </DialogContent>

        <DialogActions sx={{ p: 3, backgroundColor: '#f8f9fa' }}>
          <Button
            onClick={handleCloseViewDialog}
            variant="contained"
            sx={{
              backgroundColor: '#536DFE',
              '&:hover': { backgroundColor: '#3F51B5' },
              textTransform: 'none',
              fontWeight: 600,
              px: 3
            }}
          >
            Close
          </Button>
        </DialogActions>
      </Dialog>

    </Box>
  );
};

export default BookingCommission;

